// src/store/slices/doctorSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { doctorService } from "../../services/api/doctorService";
import { IDoctor } from "../../types/doctor";

interface DoctorState {
  doctors: IDoctor[];
  selectedDoctor: IDoctor | null;
  loading: boolean;
  selectedDoctorLoading: boolean;
  error: string | null;
  selectedDoctorError: string | null;
  lastFetched: number | null;
}

const initialState: DoctorState = {
  doctors: [],
  selectedDoctor: null,
  loading: false,
  selectedDoctorLoading: false,
  error: null,
  selectedDoctorError: null,
  lastFetched: null,
};

export const fetchAllDoctors = createAsyncThunk(
  "doctors/fetchAll",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { doctors: DoctorState };
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      if (
        state.doctors.lastFetched &&
        now - state.doctors.lastFetched < fiveMinutes &&
        state.doctors.doctors.length > 0
      ) {
        return state.doctors.doctors;
      }

      const doctors = await doctorService.getAllDoctors();
      return doctors;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch doctors"
      );
    }
  }
);

export const fetchDoctorById = createAsyncThunk(
  "doctors/fetchById",
  async (doctorId: string, { rejectWithValue }) => {
    try {
      const doctor = await doctorService.getDoctorById(doctorId);
      return doctor;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch doctor"
      );
    }
  }
);

const doctorSlice = createSlice({
  name: "doctors",
  initialState,
  reducers: {
    clearSelectedDoctor: (state) => {
      state.selectedDoctor = null;
      state.selectedDoctorError = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.selectedDoctorError = null;
    },
    invalidateCache: (state) => {
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllDoctors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllDoctors.fulfilled, (state, action) => {
        state.loading = false;
        state.doctors = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchAllDoctors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchDoctorById.pending, (state) => {
        state.selectedDoctorLoading = true;
        state.selectedDoctorError = null;
      })
      .addCase(fetchDoctorById.fulfilled, (state, action) => {
        state.selectedDoctorLoading = false;
        state.selectedDoctor = action.payload;
        const index = state.doctors.findIndex(
          (d) => d.doctorId === action.payload.doctorId
        );
        if (index !== -1) {
          state.doctors[index] = action.payload;
        }
      })
      .addCase(fetchDoctorById.rejected, (state, action) => {
        state.selectedDoctorLoading = false;
        state.selectedDoctorError = action.payload as string;
      });
  },
});

export const { clearSelectedDoctor, clearErrors, invalidateCache } =
  doctorSlice.actions;
export default doctorSlice.reducer;
